import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

export interface SplitTenderDialogData {
  totalCents: number;
}

export interface SplitTenderPortion {
  label: string;
  amountCents: number;
}

export interface SplitTenderDialogResult {
  portions: SplitTenderPortion[];
}

@Component({
  selector: 'app-split-tender-dialog',
  templateUrl: './split-tender-dialog.component.html',
  styleUrls: ['./split-tender-dialog.component.scss'],
})
export class SplitTenderDialogComponent implements OnInit {
  totalCents: number;
  portions: SplitTenderPortion[] = [];
  numPeople = 2;
  readonly MIN_PEOPLE = 2;
  readonly MAX_PEOPLE = 8;
  // Every portion has to be chargeable, so nobody can be pushed below a cent.
  private readonly MIN_PORTION_CENTS = 1;
  // Portions the cashier typed into. Editing one holds it fixed and
  // rebalances the untouched portions so the split always sums to the
  // order total — the register refuses to start a split that doesn't.
  private editedIndices = new Set<number>();

  constructor(
    private dialogRef: MatDialogRef<SplitTenderDialogComponent, SplitTenderDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) data: SplitTenderDialogData,
  ) {
    this.totalCents = data.totalCents;
  }

  ngOnInit(): void {
    this.applyEqualSplit();
  }

  applyEqualSplit(): void {
    const base = Math.floor(this.totalCents / this.numPeople);
    const last = this.totalCents - base * (this.numPeople - 1);
    const existing = this.portions;
    this.portions = Array.from({length: this.numPeople}, (_, i) => ({
      // Keep any label the cashier already typed; only the money resets.
      label: existing[i] ? existing[i].label : `Person ${i + 1}`,
      amountCents: i < this.numPeople - 1 ? base : last,
    }));
    this.editedIndices.clear();
  }

  decreasePeople(): void {
    if (this.numPeople > this.MIN_PEOPLE) {
      this.numPeople--;
      this.applyEqualSplit();
    }
  }

  increasePeople(): void {
    if (this.numPeople < this.MAX_PEOPLE) {
      this.numPeople++;
      this.applyEqualSplit();
    }
  }

  getPortionDollars(index: number): string {
    return (this.portions[index].amountCents / 100).toFixed(2);
  }

  setPortionDollars(index: number, value: string): void {
    const cents = Math.round(parseFloat(value) * 100);
    if (isNaN(cents) || !this.portions[index]) { return; }
    // The edited portion can never claim so much that another person is
    // left with nothing to charge, nor drop below a chargeable cent.
    const others = this.portions.length - 1;
    const max = this.totalCents - others * this.MIN_PORTION_CENTS;
    this.portions[index].amountCents = Math.min(Math.max(cents, this.MIN_PORTION_CENTS), max);
    this.editedIndices.add(index);
    this.rebalance(index);
  }

  /**
   * Spread whatever is left of the order total across the portions the
   * cashier has not typed into, so the split always closes out at zero
   * remaining. When every other portion has been hand-set, the oldest
   * edits are released first so there is always somewhere to absorb the
   * difference — the just-edited portion is the one that stays put.
   */
  private rebalance(justEdited: number): void {
    const all = this.portions.map((_, i) => i);
    let absorbers = all.filter(i => i !== justEdited && !this.editedIndices.has(i));
    const fixedSum = () => this.portions.reduce(
      (sum, p, i) => absorbers.indexOf(i) === -1 ? sum + p.amountCents : sum, 0);

    // No room left — either every other portion is hand-set, or the earlier
    // edits already claim so much that the absorbers cannot stay chargeable.
    // Release the older edits; the portion just typed into is the one we honour.
    if (!absorbers.length ||
        this.totalCents - fixedSum() < absorbers.length * this.MIN_PORTION_CENTS) {
      absorbers = all.filter(i => i !== justEdited);
      this.editedIndices = new Set<number>([justEdited]);
    }

    const remaining = this.totalCents - fixedSum();
    const base = Math.floor(remaining / absorbers.length);

    absorbers.forEach((portionIndex, n) => {
      const share = n === absorbers.length - 1
        ? remaining - base * (absorbers.length - 1)   // last absorber eats the rounding cent
        : base;
      this.portions[portionIndex].amountCents = Math.max(share, this.MIN_PORTION_CENTS);
    });
  }

  get sumCents(): number {
    return this.portions.reduce((s, p) => s + p.amountCents, 0);
  }

  get remainingCents(): number {
    return this.totalCents - this.sumCents;
  }

  get isValid(): boolean {
    return this.sumCents === this.totalCents && this.portions.every(p => p.amountCents > 0);
  }

  formatCents(cents: number): string {
    const abs = Math.abs(cents);
    const str = '$' + (abs / 100).toFixed(2);
    return cents < 0 ? '-' + str : str;
  }

  confirm(): void {
    if (!this.isValid) { return; }
    this.dialogRef.close({portions: [...this.portions]});
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
